import { Box, TextField } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DocumentNode, useMutation } from '@parkchirp/api-client';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { SnackBarProps } from '../../../types';
import { isOwnerPortal } from '../../../utils';
import { EditorModal } from '../../modals';

interface EditNotesModalProps {
	id: string;
	mutationToUse: DocumentNode;
	handleClose: (shouldRefetch: boolean) => void;
	existingNotes: string | null;
	headerText: string;
	// the below function, if it is sent, will return a string error message
	// if something was bad, else null if the input is valid
	validator?: (value: string) => string | null;
	setShowSnackbar: Dispatch<SetStateAction<Partial<SnackBarProps> | null>>;
}

export const EditInvoiceNotesModal = ({
	id,
	mutationToUse,
	existingNotes,
	handleClose,
	headerText,
	validator = null,
	setShowSnackbar,
}: EditNotesModalProps) => {
	const [notes, setNotes] = useState('');
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
	const [updateNotes, { loading }] = useMutation(mutationToUse);

	useEffect(() => {
		if (existingNotes) setNotes(existingNotes);
	}, [existingNotes]);

	const theme = useTheme();

	return (
		<EditorModal
			theme={theme}
			handleClose={() => handleClose(false)}
			handleSave={async () => {
				try {
					if (validator) {
						const maybeErrorMessage = validator(notes);
						if (maybeErrorMessage) {
							setShowSnackbar({
								message: maybeErrorMessage,
								severity: 'error',
								autoHideDuration: null,
							});
							return false;
						}
					}
					const response = await updateNotes({
						context: {
							apiVersion: 'v2',
						},
						variables: {
							invoiceId: id,
							notes,
						},
					});
					// the response object looks like:
					// {"data":{"updateInvoiceDetailNotes":true}}
					// the below will extract the mutation name from the object, where
					// the keys of data is ["updateInvoiceDetailNotes"].
					// if for some reason there are no keys, mutationName will be undefined.
					// also if mutationName maps to a boolean false value, it means the mutation
					// failed and we show an error to the user.
					const queryName = Object.keys(response.data)[0];
					if (!queryName || response.data[queryName] === false) {
						setShowSnackbar({
							message: 'Failed to update notes',
							severity: 'error',
							autoHideDuration: null,
						});
						return false;
					}
					setShowSnackbar({
						message: 'Successfully updated notes',
						severity: 'success',
					});
					handleClose(true);
					return true;
				} catch (error) {
					setShowSnackbar({
						message: `Unable to update notes: ${error}`,
						severity: 'error',
						autoHideDuration: null,
					});
					return false;
				}
			}}
			hasUnsavedChanges={hasUnsavedChanges}
			loading={loading}
			headerText={headerText}
			readOnly={isOwnerPortal}
		>
			<Box
				style={{
					minHeight: '300px',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
				}}
			>
				<TextField
					style={{ width: '100%' }}
					id='edit-notes-modal'
					label='Edit Notes'
					multiline
					rows={10}
					variant='filled'
					disabled={isOwnerPortal}
					value={notes}
					onChange={(e) => {
						setHasUnsavedChanges(true);
						setNotes(e.target.value);
					}}
					inputProps={{ 'data-cy': 'invoice-notes-multiline-input' }}
				/>
			</Box>
		</EditorModal>
	);
};
